import { createContext, useCallback, useContext, useMemo, useState } from 'react';

type Toast = {
  id: number;
  title?: string;
  description: string;
  variant?: 'default' | 'success' | 'error' | 'warning';
  duration?: number; // ms
  action?: { label: string; onClick: () => void };
};

type ToastContextValue = {
  toasts: Toast[];
  show: (toast: Omit<Toast, 'id'>) => void;
  dismiss: (id: number) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = Date.now() + Math.floor(Math.random() * 1000);
    const next: Toast = { id, duration: 4000, variant: 'default', ...toast };
    setToasts((prev) => [...prev, next]);
    if (next.duration && next.duration > 0) {
      window.setTimeout(() => dismiss(id), next.duration);
    }
  }, [dismiss]);

  const value = useMemo(() => ({ toasts, show, dismiss }), [toasts, show, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): { show: ToastContextValue['show'] } {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Provide a safe no-op in tests or when provider is absent
    return { show: () => {} } as { show: ToastContextValue['show'] };
  }
  return { show: ctx.show };
}

function ToastViewport({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: number) => void }): React.JSX.Element | null {
  if (!toasts.length) return null;
  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-2">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={[
            'w-80 rounded-lg shadow-lg border p-3 bg-white',
            t.variant === 'success' ? 'border-green-200' : '',
            t.variant === 'error' ? 'border-red-200' : '',
            t.variant === 'warning' ? 'border-yellow-200' : '',
          ].join(' ')}
          role="status"
          aria-live="polite"
        >
          {t.title && <div className="font-medium mb-1">{t.title}</div>}
          <div className="text-sm text-gray-700">{t.description}</div>
          <div className="mt-2 text-right flex items-center justify-end gap-2">
            {t.action && (
              <button
                className="text-xs text-primary hover:text-primary-hover"
                onClick={() => { try { t.action?.onClick(); } finally { onDismiss(t.id); } }}
              >
                {t.action.label}
              </button>
            )}
            <button
              className="text-xs text-gray-500 hover:text-gray-700"
              onClick={() => onDismiss(t.id)}
              aria-label="Dismiss notification"
            >
              Dismiss
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
